/**
 * Auftragsübersicht – reine Auswertung.
 *
 * Fast ohne Aktionen: kein Statuswechsel, kein Löschen. Wer etwas anderes
 * ändern will, geht ins Angebot. Diese Seite beantwortet vor allem „was wurde
 * beauftragt, für wie viel, und was bleibt davon".
 *
 * Eine Ausnahme gibt es: den Auftragswert. Er ist die Zahl, um die es hier
 * geht, und wer eine Liste mit falschen Werten vor sich hat, will sie dort
 * richtigstellen, wo sie ihm auffällt – nicht in zwanzig einzeln geöffneten
 * Angeboten. Jede Änderung wird mit altem Wert, neuem Wert, Person und
 * Zeitpunkt festgehalten und erscheint in der Chronik des Angebots.
 *
 * Ein beauftragtes Angebot verschwindet aus dem Vertriebscockpit – dort steht
 * nur, was beim Kunden liegt. Genau diese Lücke schließt diese Seite.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { IconMonday, IconSharepoint, IconStift } from '../komponenten/Icons';
import { Betragsfeld } from '../komponenten/Betragsfeld';
import type { Auftragsuebersicht } from '../typen';
import './auftraege.css';

type SortSpalte = 'datum' | 'nr' | 'kunde' | 'wert' | 'beitrag';

export function Auftraege() {
  const navigate = useNavigate();
  const [jahr, setJahr] = useState('');
  const [suche, setSuche] = useState('');
  const [sortSpalte, setSortSpalte] = useState<SortSpalte>('datum');
  const [absteigend, setAbsteigend] = useState(true);

  const queryClient = useQueryClient();
  /** Zeile, deren Auftragswert gerade bearbeitet wird – höchstens eine. */
  const [bearbeitet, setBearbeitet] = useState<number | null>(null);

  /**
   * Auftragswert ändern.
   *
   * Bewusst über dieselbe Route wie im Angebot: Die Auftragsübersicht bekommt
   * keinen eigenen Schreibweg. Sonst gäbe es zwei Stellen, an denen dieselbe
   * Zahl entsteht, und nur eine davon würde beim nächsten Umbau angepasst.
   */
  const wertMutation = useMutation({
    mutationFn: ({ id, auftrag }: { id: number; auftrag: number | null }) =>
      api.aendere(`/angebote/${id}/auftragswert`, { auftrag }),
    onSuccess: () => {
      setBearbeitet(null);
      // Auch die Summen, der Deckungsbeitrag und die Kategorien hängen daran.
      void queryClient.invalidateQueries({ queryKey: ['auftraege'] });
      void queryClient.invalidateQueries({ queryKey: ['angebote'] });
    },
    onError: () => setBearbeitet(null),
  });

  const abfrage = useQuery({
    queryKey: ['auftraege', jahr],
    queryFn: () =>
      api.hole<Auftragsuebersicht>(`/auftraege${jahr ? `?jahr=${encodeURIComponent(jahr)}` : ''}`),
  });

  const d = abfrage.data;

  const gefiltert = useMemo(() => {
    const liste = d?.auftraege ?? [];
    const worte = suche.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (worte.length === 0) return liste;
    return liste.filter((a) => {
      const heuhaufen = [a.kennung, a.prefix, a.kategorie, a.name, a.kunde, a.ort, a.kalkulant, a.versender]
        .join(' ')
        .toLowerCase();
      return worte.every((w) => heuhaufen.includes(w));
    });
  }, [d, suche]);

  const liste = useMemo(() => {
    const richtung = absteigend ? -1 : 1;
    const text = (s: string) => (s || '').toLocaleLowerCase('de-AT');
    return [...gefiltert].sort((a, b) => {
      let v = 0;
      switch (sortSpalte) {
        case 'nr':
          v = a.kennung.localeCompare(b.kennung, 'de-AT', { numeric: true });
          break;
        case 'kunde':
          v = text(a.kunde).localeCompare(text(b.kunde), 'de-AT');
          break;
        case 'wert':
          v = a.wert - b.wert;
          break;
        case 'beitrag':
          v = a.beitrag - b.beitrag;
          break;
        default:
          v = a.datum.localeCompare(b.datum);
      }
      if (v === 0) v = a.kennung.localeCompare(b.kennung, 'de-AT', { numeric: true });
      return v * richtung;
    });
  }, [gefiltert, sortSpalte, absteigend]);

  function sortiereNach(spalte: SortSpalte) {
    if (spalte === sortSpalte) {
      setAbsteigend((a) => !a);
      return;
    }
    setSortSpalte(spalte);
    // Beträge und Daten von groß nach klein, Namen von A nach Z.
    setAbsteigend(spalte === 'datum' || spalte === 'wert' || spalte === 'beitrag');
  }

  const sortState = { aktiv: sortSpalte, absteigend, onSortieren: sortiereNach };

  return (
    <div className="eh-inhalt auf">
      <div className="eh-kopfzeile">
        <div>
          <div className="eh-kicker">Auswertung</div>
          <div className="eh-titel">Auftragsübersicht</div>
          <div className="auf-untertitel">
            Beauftragte Angebote mit Auftragswert und erwartetem Deckungsbeitrag. Der Auftragswert
            lässt sich hier über den Stift richtigstellen; jede Änderung steht mit Person und
            Zeitpunkt in der Chronik des Angebots.
          </div>
        </div>

        {d && d.jahre.length > 1 && (
          <div className="auf-jahre">
            {d.jahre.map((j) => (
              <button
                key={j}
                type="button"
                className={`auf-jahr ${j === d.jahr ? 'aktiv' : ''}`}
                onClick={() => setJahr(j)}
              >
                {j}
              </button>
            ))}
          </div>
        )}
      </div>

      {abfrage.isLoading && <div className="eh-karte">Aufträge werden geladen …</div>}

      {d && (
        <>
          <div className="auf-kpis">
            {d.kennzahlen.map((k) => (
              <div className="auf-kpi" key={k.label}>
                <span className="auf-kpi-label">{k.label}</span>
                <span className="auf-kpi-wert" style={{ color: k.farbe }}>
                  {k.wert}
                </span>
                <span className="auf-kpi-hinweis">{k.hinweis}</span>
              </div>
            ))}
          </div>

          {d.ohneWert > 0 && (
            <div className="auf-warnung">
              {d.ohneWert} {d.ohneWert === 1 ? 'Auftrag hat' : 'Aufträge haben'} keinen
              Auftragswert – {d.ohneWert === 1 ? 'er fehlt' : 'sie fehlen'} in allen Summen dieser
              Seite. Der Wert lässt sich im jeweiligen Angebot nachtragen.
            </div>
          )}

          {d.nachKategorie.length > 0 && (
            <div className="auf-karte">
              <div>
                <div className="auf-label">Volumen je Gewerk · {d.jahr}</div>
              </div>
              <div className="auf-katliste">
                {d.nachKategorie.map((k) => (
                  <div className="auf-kat" key={k.prefix}>
                    <div className="auf-kat-kopf">
                      <span className="auf-kat-name">
                        <span className="auf-kat-prefix">{k.prefix}</span>
                        {k.label}
                        <span className="auf-kat-anzahl">
                          {k.anzahl} {k.anzahl === 1 ? 'Auftrag' : 'Aufträge'}
                        </span>
                      </span>
                      <span className="auf-kat-werte">
                        <strong>{k.wertText}</strong>
                        <span className="auf-kat-beitrag">
                          {k.ohneAufschlag ? 'kein Aufschlag hinterlegt' : `${k.beitragText} Beitrag`}
                        </span>
                      </span>
                    </div>
                    <div className="auf-kat-schiene">
                      <div className="auf-kat-fuellung" style={{ width: `${k.anteil}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="eh-kopfzeile">
            <div>
              <div className="auf-listentitel">Aufträge {d.jahr}</div>
              <div className="auf-listenhinweis">
                Datum ist der Tag der Beauftragung, nicht der Angebotslegung
              </div>
            </div>
            <label className="auf-suche">
              <span aria-hidden="true">⌕</span>
              <input
                type="search"
                value={suche}
                placeholder="Suchen: Nummer, Kunde, Gewerk …"
                aria-label="Aufträge durchsuchen"
                onChange={(e) => setSuche(e.target.value)}
              />
              {suche && (
                <button type="button" onClick={() => setSuche('')} title="Suche zurücksetzen">
                  ×
                </button>
              )}
            </label>
          </div>

          <div className="auf-tabelle">
            <div className="auf-zeile auf-kopf">
              <Kopf spalte="nr" {...sortState}>
                Auftrag
              </Kopf>
              <Kopf spalte="kunde" {...sortState}>
                Kunde
              </Kopf>
              <Kopf spalte="datum" {...sortState}>
                Beauftragt
              </Kopf>
              <Kopf spalte="wert" {...sortState}>
                Auftragswert
              </Kopf>
              <Kopf spalte="beitrag" {...sortState}>
                Beitrag
              </Kopf>
              <div className="auf-sp-links">Links</div>
            </div>

            {liste.map((a) => (
              <div className="auf-zeile auf-datenzeile" key={a.id}>
                <button
                  type="button"
                  className="auf-name"
                  onClick={() => navigate(`/angebot/${a.id}`, { state: { herkunft: 'auftraege' } })}
                  title={`${a.name} – im Angebot öffnen`}
                >
                  {a.kennung}_{a.prefix}
                </button>
                <div className="auf-kunde" title={a.ort}>
                  {a.kunde}
                </div>
                <div className="auf-datum">{a.datumText || '—'}</div>
                <div className="auf-wert">
                  {bearbeitet === a.id ? (
                    <Betragsfeld
                      wert={a.ohneWert ? null : a.wert}
                      autoFokus
                      laeuft={wertMutation.isPending}
                      onSpeichern={(neu) => wertMutation.mutate({ id: a.id, auftrag: neu })}
                      onAbbrechen={() => setBearbeitet(null)}
                    />
                  ) : (
                    <button
                      type="button"
                      className="auf-wert-knopf"
                      title="Auftragswert ändern"
                      onClick={() => setBearbeitet(a.id)}
                    >
                      {a.ohneWert ? <span className="auf-fehlt">nicht erfasst</span> : a.wertText}
                      <span className="auf-stift">
                        <IconStift groesse={13} />
                      </span>
                    </button>
                  )}
                </div>
                <div className="auf-wert auf-beitrag" title={a.margeText ? `Marge ${a.margeText}` : ''}>
                  {a.ohneAufschlag ? <span className="auf-fehlt">—</span> : a.beitragText}
                </div>
                <div className="auf-links auf-sp-links">
                  {a.mondayUrl ? (
                    <a href={a.mondayUrl} target="_blank" rel="noreferrer" title="Monday-Item">
                      <IconMonday />
                    </a>
                  ) : (
                    <span className="auf-link-leer" title="Kein Monday-Item verknüpft">
                      <IconMonday />
                    </span>
                  )}
                  {a.sharepointUrl ? (
                    <a href={a.sharepointUrl} target="_blank" rel="noreferrer" title="SharePoint-Ordner">
                      <IconSharepoint />
                    </a>
                  ) : (
                    <span className="auf-link-leer" title="Kein SharePoint-Ordner verknüpft">
                      <IconSharepoint />
                    </span>
                  )}
                </div>
              </div>
            ))}

            {liste.length === 0 && (
              <div className="auf-leer">
                {suche
                  ? `Kein Treffer für „${suche}" unter ${d.auftraege.length} Aufträgen.`
                  : `Für ${d.jahr} ist noch kein Auftrag erfasst.`}
              </div>
            )}
          </div>

          {suche && liste.length > 0 && (
            <div className="auf-trefferzeile">
              {liste.length} von {d.auftraege.length} angezeigt
              <button type="button" onClick={() => setSuche('')}>
                alle anzeigen
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** Klickbarer Spaltenkopf – wie im Cockpit. */
function Kopf({
  spalte,
  aktiv,
  absteigend,
  onSortieren,
  children,
}: {
  spalte: SortSpalte;
  aktiv: SortSpalte;
  absteigend: boolean;
  onSortieren: (s: SortSpalte) => void;
  children: React.ReactNode;
}) {
  const istAktiv = aktiv === spalte;
  return (
    <button
      type="button"
      className={`auf-kopfknopf ${istAktiv ? 'aktiv' : ''}`}
      onClick={() => onSortieren(spalte)}
      aria-sort={istAktiv ? (absteigend ? 'descending' : 'ascending') : 'none'}
      title={istAktiv ? 'Richtung umkehren' : 'Nach dieser Spalte sortieren'}
    >
      {children}
      <span className="auf-pfeil" aria-hidden="true">
        {istAktiv ? (absteigend ? '▾' : '▴') : ''}
      </span>
    </button>
  );
}
